<script lang="ts" setup>
import { reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useAuthStore from '@/stores/auth';
import ApiService from '@/services/api_service';
import { TokenService } from '@/services/token_service';
import { EchoService } from '@/services/web_socket';
import { PropertyService } from '@/services/property_service';
import { AuthService } from '@/services/auth_service';
import { i18n, loadLocaleMessages, setI18nLanguage } from '@/plugins/i18n';
import { useNotifications } from '@/composables/notifications';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { t } = useI18n();

const authStore = useAuthStore();
const router = useRouter();
const {
  errorNotification,
} = useNotifications();
const is2FAActive = ref(false);
const authInfo = ref({});
const type = ref('otp');

const form = reactive({
  username: import.meta.env.VITE_APP_USERNAME,
  password: import.meta.env.VITE_APP_PASSWORD,
});

const getUserPermissionMap = (userRole: string) => {
  let permissionRouteMap = [];
  switch (userRole) {
    case 'owner':
      permissionRouteMap = [
        { permission: 'owner_dashboard_view', route: 'owner.dashboard' },
        { permission: 'owner_reservation_view', route: 'owner.reservation.map' },
        { permission: 'owner_reservation_view', route: 'owner.reservation.index' },
        { permission: 'owner_view_ci_co_page', route: 'owner.check_in_out' },
        { permission: 'owner_invoicing_view', route: 'owner.invoices.issued' },
        { permission: 'owner_calendar_view', route: 'owner.rates.edit' },
        { permission: 'owner_accommodation_view', route: 'owner.accommodation.index' },
        { permission: 'owner_finance_expenses_view', route: 'owner.finances.expenses' },
        { permission: 'owner_finance_invoices_view', route: 'owner.finances.invoices' },
        { permission: 'owner_owners_view', route: 'owner.owners.owner' },
        { permission: 'owner_stats_indicators_view', route: 'owner.statistics.indicators' },
        { permission: 'owner_stats_reservations_view', route: 'owner.statistics.reservations' },
        { permission: 'owner_communications_livechat_view', route: 'owner.live_chat' },
        { permission: 'owner_marketplace_view', route: 'owner.marketplace' },
      ];
      break;
    case 'welcomer':
      permissionRouteMap = [
        { permission: 'welcomer_dashboard_view', route: 'welcomer.dashboard' },
        { permission: 'welcomer_reservation_view', route: 'welcomer.reservation.index' },
        { permission: 'welcomer_view_ci_co_page', route: 'tasks.check_in_outs' },
        { permission: 'welcomer_sef_view', route: 'welcomer.sef' },
        { permission: 'welcomer_maintenance_view', route: 'welcomer.maintenance' },
        { permission: 'welcomer_ci_online_view', route: 'welcomer.digitalConcierge' },
        { permission: 'welcomer_communications_livechat_view', route: 'welcomer.live_chat' },
        { permission: 'welcomer_marketplace_view', route: 'welcomer.marketplace' },
        { permission: 'welcomer_networking_view', route: 'welcomer.networking' },
      ];
      break;
    case 'cleaner':
      permissionRouteMap = [
        { permission: 'housekeeping_housekeeping_view', route: 'housekeeping.housekeeping_list' },
        { permission: 'housekeeping_maintenance_view', route: 'housekeeping.maintenance' },
        { permission: 'housekeeping_stats_tasks_housekeeping_view', route: 'housekeeping.statistics' },
        { permission: 'housekeeping_communications_livechat_view', route: 'housekeeping.live_chat' },
        { permission: 'housekeeping_marketplace_view', route: 'housekeeping.marketplace' },
        { permission: 'housekeeping_networking_view', route: 'housekeeping.networking' },
      ];
      break;
    case 'maintenance':
      permissionRouteMap = [
        { permission: 'maintenance_maintenance_view', route: 'maintenances.maintenance' },
        { permission: 'maintenance_stats_tasks_maintenance_view', route: 'maintenances.statistics' },
        { permission: 'maintenance_communications_livechat_view', route: 'maintenances.live_chat' },
        { permission: 'maintenance_marketplace_view', route: 'maintenances.marketplace' },
        { permission: 'maintenance_networking_view', route: 'maintenances.networking' }
      ];
      break;
    default:
      permissionRouteMap = [
        { permission: 'dashboard_view', route: 'dashboard' },
        { permission: 'reservation_view', route: 'reservations.map' },
        { permission: 'view_ci_co_page', route: 'tasks.check_in_outs' },
        { permission: 'housekeeping_view', route: 'tasks.cleaning' },
        { permission: 'sef_view', route: 'tasks.sef' },
        { permission: 'maintenance_view', route: 'tasks.maintenance' },
        { permission: 'guest_view', route: 'guests' },
        { permission: 'invoicing_view', route: 'invoicing.issued' },
        { permission: 'ci_online_view', route: 'tasks.check_in_online' },
        { permission: 'calendar_view', route: 'rates.price_map' },
        { permission: 'accommodation_view', route: 'accommodation.index' },
        { permission: 'finance_profitability_view', route: 'finance.rentability' },
        { permission: 'finance_income_view', route: 'finance.income' },
        { permission: 'finance_expenses_view', route: 'finance.expenses' },
        { permission: 'finance_invoices_view', route: 'finance.invoices' },
        { permission: 'finance_payments_view', route: 'finance.payments' },
        { permission: 'owners_view', route: 'owners.owner' },
        { permission: 'owner_finance_expenses_view', route: 'finance' },
        { permission: 'stats_indicators_view', route: 'indicators' },
        { permission: 'stats_reservations_view', route: 'reservation' },
        { permission: 'stats_tasks_housekeeping_view', route: 'task' },
        { permission: 'stats_tasks_staff_view', route: 'task' },
        { permission: 'stats_tasks_maintenance_view', route: 'task' },
        { permission: 'stats_inst_sef_view', route: 'institutional' },
        { permission: 'stats_inst_tmt_view', route: 'institutional' },
        { permission: 'stats_inst_ine_srea_edit', route: 'institutional' },
        { permission: 'assistant_view', route: 'assistant' },
        { permission: 'communications_reminders_view', route: 'reminders' },
        { permission: 'communications_notes_view', route: 'notes' },
        { permission: 'communications_emails_view', route: 'messages.emails' },
        { permission: 'communications_sms_view', route: 'messages.sms' },
        { permission: 'communications_livechat_view', route: 'messages.live_chat' },
        { permission: 'communications_whatsapp_view', route: 'messages.whatsapp' },
        { permission: 'timeline_view', route: 'timeline' },
        { permission: 'marketplace_view', route: 'marketplace' },
        { permission: 'networking_view', route: 'networking' }
      ];
      break;
  }
  // Itera sobre o mapa e encontra a primeira página a que o usuário tem acesso
  return permissionRouteMap/*.find(entry => authStore.hasPermission(entry.permission))*/;
};

const loginAction = async (response: any) => {
  TokenService.saveToken(response.access_token);
  TokenService.saveRefreshToken(response.refresh_token);

  ApiService.setHeader();

  ApiService.mount401Interceptor();

  // authStore.set2FAStatus(response.two_fa);

  EchoService.connect();

  await AuthService.getUser();
  await PropertyService.getProperties();

  if (authStore.role === 'super_admin' || authStore.role === 'admin') {
    router.replace({
      name: 'dashboard',
    });
  }
  else {
    const preference = await authStore.getPreference('auth_redirect_route');
    const userPermissionMap = getUserPermissionMap(authStore.role, preference);

    let targetRoute = userPermissionMap.find(entry => entry.route === preference);

    if(targetRoute && authStore.hasPermission(targetRoute.permission)) {
      await router.replace({
        name: targetRoute.route
      });
    } else {
      targetRoute = userPermissionMap.find(entry => authStore.hasPermission(entry.permission));

      if(targetRoute) {
        await router.replace({
          name: targetRoute.route
        });
      } else {
        errorNotification({ message: t('default_messages.no_permissions') });
        await AuthService.signOut();
      }
    }
  }
};

const handleLogin = async () => {
  authStore.signIn(form)
    .then(async (response: any) => {
      form.username = '';
      form.password = '';

      if (response.two_fa === 'validate') {
        authInfo.value = response;

        is2FAActive.value = true;
        type.value = response.type;

        return;
      }

      await loginAction(response);

    })
    .catch(async (err: any) => {
      // const errorAlert = await alertController
      //   .create({
      //     header: 'Failed',
      //     subHeader: 'Sign in Failed',
      //     message: err,
      //     buttons: ['OK'],
      //   });
      // await errorAlert.present();
    });
};
const updateTwoFASuccess = async (status: boolean) => {
  if (status) {
    await loginAction(status);
  }
};

const lang = ref('pt');

const changeLang = async () => {
  await loadLocaleMessages(i18n, lang.value);
  setI18nLanguage(i18n, lang.value);
};
const langs = ref(JSON.parse(import.meta.env.VITE_APP_TRANSLATIONS));
</script>

<template>
  <div class="relative">
    <!-- Lang Switcher -->
    <div class="absolute right-10 top-2">
      <el-select v-model="lang" size="default" @change="changeLang">
        <template #prefix>
          <el-image class="prefix" style="width: 20px; height: 20px" :src="`images\\flags\\${lang}.png`" fit="scale-down" />
          <span class="ml-1">{{ t(`languages.${lang}`) }}</span>
        </template>
        <el-option
          v-for="item in langs"
          :key="`images\\flags\\${item}.png`"
          :label="t(`languages.${item}`)"
          :value="item"
        >
          <div class="flex items-center">
            <el-image style="width: 20px; height: 20px" :src="`images\\flags\\${item}.png`" fit="scale-down" />
            <span class="ml-2">{{ t(`languages.${item}`) }}</span>
          </div>
        </el-option>
      </el-select>
    </div>

    <div class="min-h-screen flex flex-col bg-blue-155">
      <!-- Login form -->
      <div class="w-[calc(100vw-10%)] sm:w-[400px] ml-[5%] sm:ml-[25%] z-50 mt-28">
        <!-- Logo -->
        <div class="sm:w-[250px] z-50 mb-12 flex justify-center mx-auto">
          <img src="../../assets/login_logo.png">
        </div>

        <Card id="login-card">
          <LoginForm
            v-if="!is2FAActive"
            v-model:username="form.username"
            v-model:password="form.password"
            @handle-login="handleLogin"
          />

          <TwoFAValidate
            v-if="is2FAActive"
            :access-token="authInfo.access_token"
            :lang="lang"
            :request-type="type"
            @two-f-a-success="updateTwoFASuccess"
          />
        </Card>
      </div>

      <!-- Slogan -->
      <div class="text-gray-y9-0 whitespace-nowrap mt-3 w-[calc(100vw-10%)] sm:w-[400px] mx-[5%] sm:ml-[25%] z-50">
        <span class="fs-9 flex flex-row items-center justify-center">Developed in <img src="/images/flags/pt.png" :width="14" :height="12" class="!mx-[3px] rounded-[2px]"> with
          <span class="text-blue-y9-1 mx-1">
            <FontAwesomeIcon :icon="['fas', 'heart']" />
          </span>
          <span>by <a href="https://ynnov.pt" target="_blank" class="underline text-[9px]"><b>Ynnov</b></a></span>
        </span>
      </div>

      <!-- Bottom -->
      <div class="flex">
        <div class="absolute bottom-0 left-0 w-1/3 ml-10">
          <img src="../../assets/login_img1.svg">
        </div>
        <div class="absolute bottom-0 right-0 w-2/3 mx-10">
          <img src="../../assets/login_img2.svg">
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  #login-card{
    box-shadow: 0 0 5px rgba(175,175,175,.5), inset 0 1px 0 hsla(0,0%,100%,.9) !important;
  }

  .forgot-password-link:hover{
    color: #666666 !important;
  }
  .login-button{
    margin-top: 50px;
  }

  .el-input__wrapper{
    background-color: white;
  }
</style>
